import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { useProduct, PRODUCT_META } from "@/lib/product-context";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowRight, LogOut, ShieldCheck } from "lucide-react";
import { KlysticLogo, TAGLINE } from "@/components/KlysticBrand";

export default function KlysticHubPage() {
  const nav = useNavigate();
  const { user, signOut } = useAuth();
  const { setProduct } = useProduct();
  const [productStats, setProductStats] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const list = await api.listProducts();
        const map = {};
        list.forEach((p) => { map[p.product] = p; });
        setProductStats(map);
      } catch { /* silent */ }
    })();
  }, []);

  const pick = (key) => {
    setProduct(key);
    nav("/dashboard");
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-white">
      {/* Klystic brand background — soft blue→teal→green waves */}
      <div
        className="absolute inset-0 pointer-events-none opacity-90"
        style={{
          background:
            "radial-gradient(60% 50% at 0% 100%, rgba(31,111,235,0.28) 0%, rgba(31,111,235,0) 60%)," +
            "radial-gradient(50% 45% at 100% 100%, rgba(31,203,134,0.28) 0%, rgba(31,203,134,0) 60%)," +
            "radial-gradient(55% 45% at 50% 100%, rgba(17,181,207,0.24) 0%, rgba(17,181,207,0) 60%)",
        }}
      />
      <svg
        className="absolute bottom-0 left-0 w-full h-[55%] pointer-events-none"
        viewBox="0 0 1440 600"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="wave1" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#1F6FEB" stopOpacity="0.30" />
            <stop offset="55%" stopColor="#11B5CF" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#1FCB86" stopOpacity="0.22" />
          </linearGradient>
          <linearGradient id="wave2" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#1F6FEB" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#1FCB86" stopOpacity="0.12" />
          </linearGradient>
        </defs>
        <path
          d="M0 380 C 240 300, 480 500, 720 380 S 1200 300, 1440 400 L1440 600 L0 600 Z"
          fill="url(#wave1)"
        />
        <path
          d="M0 460 C 240 400, 480 560, 720 460 S 1200 400, 1440 480 L1440 600 L0 600 Z"
          fill="url(#wave2)"
        />
      </svg>

      {/* Top bar */}
      <header className="relative z-10 max-w-7xl mx-auto px-6 h-20 flex items-center justify-between gap-4">
        <KlysticLogo symbolClass="h-8 sm:h-10" wordmarkClass="h-3.5 sm:h-5" label={TAGLINE} testId="hub-logo" />
        {user && (
          <div className="flex items-center gap-3">
            {user.role === "admin" && (
              <span className="hidden md:inline-flex items-center gap-1 text-[10px] font-mono tracking-widest px-2 py-1 bg-klystic-ink/10 text-klystic-ink">
                <ShieldCheck size={10} /> ADMIN · {user.email}
              </span>
            )}
            <Button
              variant="outline"
              onClick={() => { signOut(); nav("/login", { replace: true }); }}
              className="rounded-none h-9 border-slate-300 shrink-0"
              data-testid="hub-signout"
            >
              <LogOut size={14} className="mr-2" /> Sign out
            </Button>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pt-8 md:pt-16 pb-12">
        <div className="max-w-3xl">
          <div className="inline-block overline text-klystic-sky mb-3">Klystic · UAT Tracker</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-klystic-ink leading-[1.05]">
            Pick a product to test.
          </h1>
          <p className="mt-5 text-slate-600 text-base max-w-xl leading-relaxed">
            Two products live under Klystic today. Each has its own script, its own
            environment, and its own bucket of results — so nothing bleeds across
            teams.
          </p>
        </div>
      </section>

      {/* Product picker */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ProductCard
            testId="hub-pick-keplerio"
            meta={PRODUCT_META.keplerio}
            stats={productStats.keplerio}
            onClick={() => pick("keplerio")}
          />
          <ProductCard
            testId="hub-pick-tarklen"
            meta={PRODUCT_META.tarklen}
            stats={productStats.tarklen}
            onClick={() => pick("tarklen")}
          />
        </div>
      </section>
    </div>
  );
}

function ProductCard({ meta, stats, onClick, testId }) {
  const isKeplerio = meta.key === "keplerio";
  const container = isKeplerio
    ? "bg-white text-slate-900 border border-slate-200"
    : "bg-white text-slate-900 border border-slate-200";
  const accentBar = isKeplerio ? meta.swoosh : "bg-tarklen-green";
  const accentDot = isKeplerio ? "bg-keplerio-teal" : "bg-tarklen-purple";
  const btnClass = isKeplerio
    ? "bg-keplerio-navy text-white hover:bg-keplerio-navy-dark"
    : "bg-tarklen-blue text-white hover:bg-tarklen-blue-dark";
  const envBadge = isKeplerio
    ? "bg-keplerio-gold/15 text-keplerio-gold-dark border border-keplerio-gold/40"
    : "bg-tarklen-green/15 text-tarklen-green-dark border border-tarklen-green/30";
  const bgOverlay = (
    <div
      className="absolute inset-0 opacity-30 pointer-events-none"
      style={{
        background: isKeplerio
          ? "radial-gradient(60% 50% at 0% 0%, rgba(255,165,42,0.22) 0%, transparent 60%)," +
            "radial-gradient(50% 50% at 100% 100%, rgba(10,155,240,0.22) 0%, transparent 60%)"
          : "radial-gradient(60% 50% at 0% 0%, rgba(46,123,255,0.30) 0%, transparent 60%)," +
            "radial-gradient(50% 50% at 100% 100%, rgba(122,60,255,0.30) 0%, transparent 60%)," +
            "radial-gradient(40% 40% at 100% 0%, rgba(38,193,103,0.25) 0%, transparent 60%)",
      }}
    />
  );

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`relative overflow-hidden text-left p-8 md:p-10 ${container} group transition-transform hover:-translate-y-0.5`}
    >
      {bgOverlay}
      <div className={`absolute top-0 left-0 w-full h-1 ${accentBar}`} />
      <div className={`absolute bottom-0 right-0 w-1 h-24 ${accentDot}`} />
      <div className="relative flex items-start justify-between gap-4">
        <img
          src={meta.logo}
          alt={meta.name}
          className="h-12 w-auto"
          data-testid={`${testId}-logo`}
        />
        <span className={`inline-block text-[10px] font-mono tracking-widest px-2 py-1 ${envBadge}`}>
          {meta.environment}
        </span>
      </div>

      <div className="relative mt-8">
        <div className="overline mb-2 opacity-70">Product</div>
        <div className="font-heading text-3xl md:text-4xl font-bold tracking-tight">
          {meta.name}
        </div>
        <p className="mt-2 text-sm text-slate-600">
          {meta.tagline}
        </p>
      </div>

      <div className="relative mt-8 flex items-center justify-between">
        <div className="flex gap-6">
          <Stat
            value={stats?.total_cases ?? "—"}
            label="Cases"
          />
          <Stat
            value={stats?.section_count ?? "—"}
            label={meta.key === "keplerio" ? "Personas" : "Sections"}
          />
        </div>
        <span
          className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium ${btnClass}`}
        >
          Start testing <ArrowRight size={14} />
        </span>
      </div>
    </button>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <div className="font-heading text-2xl font-bold text-slate-900">
        {value}
      </div>
      <div className="overline mt-0.5">{label}</div>
    </div>
  );
}
